import { gql } from "@apollo/client/core";
import { assetFragment, materialFragment } from "./services/asset";
import { optionFragment } from "./services/option";

export const activeIntegrationFragment = gql`
    fragment ActiveIntegrationFields on Integration {
        id
        logo
        name
        theme {
            id
            configuration
            primaryColor
            secondaryColor
            textColor
        }
        partner {
            id
            name
            activeAddons
            currencyCode
        }
    }
`;

export const additionalProductFragment = gql`
    fragment AdditionalIntegrationProductFields on IntegrationProduct {
        id
        product {
            id
            basePrice
            enabled
            name
            description
            sku
            skuCode
            weight
            imageUrl
            modelUrl
            overlayImageUrl
            preloadImageUrl
        }
    }
`;

export const recipientFragment = gql`
    fragment RecipientFields on Recipient {
        id
        firstName
        lastName
        address
        suburb
        state
        email
        postalCode
        country
        mobile
        company
        apartment
        customField1
        customField2
        customField3
        customField4
        customField5
        conversionConfigurationId
    }
`;

export const conversionConfigurationFragment = gql`
    fragment ConversionConfigurationFields on ConversionConfiguration {
        id
        partnerId
        name
        locations
        mandatory
        requestedData
        requestedDataItems {
            type
            mandatory
            title
            uniqueIdentifier
            customFieldIndex
            optionId
            option {
                id
                name
                variants {
                    id
                    name
                    enabled
                }
                defaultVariant {
                    id
                }
            }
        }
    }
`;

export const createManyTransactionsMutation = gql`
    ${recipientFragment}
    mutation CreateTransactions(
        $inputs: [TransactionCreateInput]!
        $marketplaceThemeInstallId: String
        $marketplaceThemeInstallConfigurationId: String
    ) {
        transactionCreateMany(
            inputs: $inputs
            marketplaceThemeInstallId: $marketplaceThemeInstallId
            marketplaceThemeInstallConfigurationId: $marketplaceThemeInstallConfigurationId
        ) {
            id
            designName
            transactionOwnerId
            workflowId
            customLogoLink
            quantity
            workflowFooterLogoLink
            restApiIntegrationLink
            workflowState
            workflowViewerLink
            workflowViewerReadOnlyLink
            recipient {
                ...RecipientFields
            }
            integrationProductId
        }
    }
`;

export const stakeholderFragment = gql`
    fragment StakeholderFields on Stakeholder {
        id
        type
        authorizationDate
        authorizationNote
        authorizationStatus
    }
`;

export const transactionShareActionFragment = gql`
    fragment TransactionShareActionFields on TransactionShareAction {
        id
        title
        type
        stakeholderType
        precedence
        url
    }
`;

export const readIntegrationProductsQuery = gql`
    ${additionalProductFragment}
    ${conversionConfigurationFragment}
    ${activeIntegrationFragment}
    query ReadIntegrationProducts($ids: [String!]!) {
        integrationProducts(ids: $ids) {
            id
            externalProductId
            externalVariantId
            additionalExternalProductId
            additionalExternalVariantId
            additionalIntegrationProduct {
                ...AdditionalIntegrationProductFields
            }
            product {
                id
                basePrice
                enabled
                minimumOrderQuantity
                name
                description
                partner {
                    id
                    name
                    currencyCode
                    customerDetailsPromptMarkdown
                }
                productImages {
                    id
                    precedence
                    asset {
                        key
                        fileLink
                        name
                        type
                        versions {
                            name
                            link
                        }
                    }
                }
                integrationProducts {
                    id
                    integration {
                        id
                        type
                    }
                    externalProductId
                    externalVariantId
                    additionalExternalProductId
                    additionalExternalVariantId
                    additionalIntegrationProduct {
                        ...AdditionalIntegrationProductFields
                    }
                }
                profanities {
                    id
                    word
                }
                sku
                skuCode
                weight
                workflows {
                    id
                    friendlyName
                    isPresent
                    workflowName
                    imageUrl
                }
                imageUrl
                modelUrl
                overlayImageUrl
                preloadImageUrl
                promptForCustomerDetails
                conversionConfiguration {
                    ...ConversionConfigurationFields
                }
                productTags {
                    id
                    name
                }
                priceBreaks {
                    id
                    minQty
                    percentage
                }
            }
            integration {
                ...ActiveIntegrationFields
            }
        }
    }
`;

export const readTransactionsQuery = gql`
    ${stakeholderFragment}
    ${transactionShareActionFragment}
    ${recipientFragment}
    query ReadTransactions($ids: [String]!) {
        transactions(ids: $ids) {
            customLogoLink
            designName
            externalDesignProductId
            externalDesignProductVariantId
            externalCartProductId
            externalCartProductVariantId
            id
            completed
            lastSyncedAt
            priceModifierTotal
            restApiIntegrationLink
            workflowFooterLogoLink
            workflowId
            workflowState
            workflowViewerLink
            workflowViewerReadOnlyLink
            previewImageLink
            isOrdered
            quantity
            printFileUrl1
            printFileUrl2
            printFileUrl3
            printFileUrl4
            printFileUrl5
            printFileName1
            printFileName2
            printFileName3
            printFileName4
            printFileName5
            stakeholders {
                ...StakeholderFields
            }
            currentStakeholder {
                ...StakeholderFields
            }
            hasAuthenticatedActions
            transactionShareActions {
                ...TransactionShareActionFields
            }
            addressValidationStatus
            recipient {
                ...RecipientFields
            }
            integrationProductId
        }
    }
`;

export const updateTransactionWorkflowStateQuery = gql`
    mutation UpdateTransactionWorkflowState($id: String!, $workflowState: String!) {
        transactionUpdate(id: $id, workflowState: $workflowState) {
            id
        }
    }
`;

export const updateTransactionQuantityQuery = gql`
    mutation UpdateTransactionQuantity($id: String!, $quantity: Int) {
        transactionUpdate(id: $id, quantity: $quantity) {
            id
        }
    }
`;

export const updateTransactionWorkflowQuery = gql`
    mutation UpdateTransactionWorkflow($id: String!, $workflowId: String!) {
        transactionUpdate(id: $id, workflowId: $workflowId) {
            id
        }
    }
`;

export const getConversionConfigurationQuery = gql`
    ${conversionConfigurationFragment}
    query ReadConversionConfigurationQuery($id: String!) {
        conversionConfiguration(id: $id) {
            ...ConversionConfigurationFields
        }
    }
`;

export const getTransactionQuery = gql`
    ${conversionConfigurationFragment}
    query ReadTransactionForDesignCreation($id: String!) {
        transactions(ids: [$id]) {
            id
            completed
            createdAt
            designName
            product {
                name
                sku
                basePrice
                minimumOrderQuantity
                promptForCustomerDetails
                partner {
                    id
                    name
                    customerDetailsPromptMarkdown
                }
                conversionConfiguration {
                    ...ConversionConfigurationFields
                }
                productTags {
                    id
                    name
                }
                priceBreaks {
                    id
                    minQty
                    percentage
                }
            }
            bundle {
                id
                metadata {
                    key
                    value
                }
                bundleStakeholders {
                    type
                    customer {
                        emailAddress
                    }
                }
            }
            externalCartProductId
            externalCartProductVariantId
            externalDesignProductId
            externalDesignProductVariantId
            lastSyncedAt
            previewImageLink
            priceModifierTotal
            quantity
            workflowId
            workflowState
            workflowViewerLink
            workflowViewerReadOnlyLink
        }
    }
`;

export const getTransactionShareActionsQuery = gql`
    ${transactionShareActionFragment}
    query ReadTransactionForShareActions($id: String!) {
        transactions(ids: [$id]) {
            id
            hasAuthenticatedActions
            transactionShareActions {
                ...TransactionShareActionFields
            }
        }
    }
`;

export const readWorkflowStates = gql`
    query ReadWorkflowStates($ids: [String]!) {
        transactions(ids: $ids) {
            id
            workflowState
        }
    }
`;

const getIntegrationProductFragment = (fetchIntegrationProducts?: boolean) => gql`
    ${additionalProductFragment}
    fragment IntegrationProductFields on IntegrationProduct {
        id
        externalProductId
        externalVariantId
        additionalExternalProductId
        additionalExternalVariantId
        additionalIntegrationProduct {
            ...AdditionalIntegrationProductFields
        }
        integration {
            id
            enabled
            externalIntegrationId
            type
            isCurrent
        }
        product {
            id
            basePrice
            enabled
            minimumOrderQuantity
            imageUrl
            modelUrl
            name
            description
            overlayImageUrl
            preloadImageUrl
            sku
            skuCode
            workflows {
                id
                index
                friendlyName
                isPresent
                workflowName
                imageUrl
            }
            productTags {
                id
                name
            }
            productImages {
                id
                precedence
                asset {
                    key
                    fileLink
                    name
                    type
                    versions {
                        name
                        link
                    }
                }
            }
            priceBreaks {
                id
                minQty
                percentage
            }
            ${
                fetchIntegrationProducts
                    ? `
                integrationProducts {
                    id
                    externalProductId
                    externalVariantId
                    additionalExternalProductId
                    additionalExternalVariantId
                    additionalIntegrationProduct {
                        ...AdditionalIntegrationProductFields
                    }
                    integration {
                        id
                        enabled
                        externalIntegrationId
                        type
                        isCurrent
                    }
                }
            `
                    : ""
            }
        }
    }
`;

export const getIntegrationProductsQuery = (includeIntegrations?: boolean) => gql`
    ${getIntegrationProductFragment(includeIntegrations)}
    query GetIntegrationProducts($ids: [String!]!) {
        integrationProducts(ids: $ids) {
            ...IntegrationProductFields
        }
    }
`;

export const getIntegrationProductFromExternalIdsQuery = (includeIntegrations?: boolean) => gql`
    ${getIntegrationProductFragment(includeIntegrations)}
    query GetIntegrationProductFromExternalIds($externalIntegrationId: String!, $externalProductId: String!) {
        integrationProductFromExternalIds(
            externalIntegrationId: $externalIntegrationId
            externalProductId: $externalProductId
        ) {
            ...IntegrationProductFields
        }
    }
`;

export const getTransactionStakeholdersQuery = gql`
    ${stakeholderFragment}
    query ReadTransactionForStakeholders($id: String!) {
        transactions(ids: [$id]) {
            stakeholders {
                ...StakeholderFields
            }
            currentStakeholder {
                ...StakeholderFields
            }
        }
    }
`;

export const finalizeUpdateTransactionMutation = gql`
    mutation FinalizeUpdateTransaction($transactionId: String!) {
        transactionFinalizeUpdate(transactionId: $transactionId)
    }
`;

export const regionFragment = gql`
    fragment RegionFields on Region {
        width
        top
        left
        height
        layer
        layerIndex
        rotation
        panelId
        immutable
        adjustmentBoundary {
            width
            x
            y
            height
        }
    }
`;

export const lookAtAnimationFragment = gql`
    fragment LookAtAnimationFields on LookAtAnimation {
        latDeg
        lonDeg
        radius
        target {
            x
            y
            z
        }
    }
`;

export const renderingPipelineConfigurationFragment = gql`
    fragment RenderingPipelineConfigurationFields on RenderingPipelineConfiguration {
        antiAliasing {
            samples
            fxaaEnabled
        }
        bloom {
            enabled
            kernel
            scale
            threshold
            weight
        }
        chromaticAberration {
            enabled
            aberrationAmount
            direction {
                x
                y
            }
            radialIntensity
        }
        colorCurves {
            enabled
            globalDensity
            globalExposure
            globalHue
            globalSaturation
            highlightsDensity
            highlightsExposure
            highlightsHue
            highlightsSaturation
            midtonesDensity
            midtonesExposure
            midtonesHue
            midtonesSaturation
            shadowsDensity
            shadowsExposure
            shadowsHue
            shadowsSaturation
        }
        depthOfField {
            enabled
            blurLevel
            focusDistance
            focalLength
            fStop
            lensSize
        }
        grain {
            enabled
            animated
            intensity
        }
        misc {
            contrast
            exposure
            toneMappingEnabled
            toneMappingType
        }
        sharpen {
            enabled
            colorAmount
            edgeAmount
        }
        vignette {
            enabled
            blendMode
            cameraFov
            center {
                x
                y
            }
            colorRgba {
                r
                g
                b
                a
            }
            colorHex
            stretch
            weight
        }
    }
`;

export const modelAnimationFragment = gql`
    fragment ModelAnimationFields on ModelAnimation {
        from
        to
        loop
        name
    }
`;

export const getWorkflowsQuery = (includeAssetMetadata: boolean) => {
    return gql`
        ${assetFragment(includeAssetMetadata)}
        ${materialFragment}
        ${optionFragment}
        ${regionFragment}
        ${lookAtAnimationFragment}
        ${renderingPipelineConfigurationFragment}
        ${modelAnimationFragment}
        query GetWorkflows($ids: [String!]!) {
            workflows(ids: $ids) {
                partner {
                    termsMarkdown
                }
                allowProofDownload
                defaultPreviewPanelIndex
                finalizeStepConfig {
                    termsMarkdown
                    lookAtAnimation {
                        ...LookAtAnimationFields
                    }
                    modelAnimation {
                        ...ModelAnimationFields
                    }
                }
                globalPreviewConfig {
                    autoRotation
                    backgroundColor
                    backgroundImage
                    clearColor
                    disableActionBar
                    disableAutomaticOrientation
                    emissiveGlowIntensity
                    enableAR
                    environmentFile
                    environmentIntensity
                    environmentRotationY
                    idleTimeBeforeRotation
                    lowerAlphaLimitDeg
                    lowerBetaLimitDeg
                    maxZoomOverride
                    minZoomOverride
                    noPan
                    upperAlphaLimitDeg
                    upperBetaLimitDeg
                    renderingPipelineConfiguration {
                        ...RenderingPipelineConfigurationFields
                    }
                }
                id
                introduction
                name
                overrideTheme {
                    id
                    configuration
                    name
                    primaryColor
                    secondaryColor
                    textColor
                    themeLayout {
                        id
                        name
                    }
                    fontAsset {
                        ...AssetFields
                    }
                }
                panels {
                    editableArea {
                        height
                        width
                        x
                        y
                    }
                    height
                    width
                    index
                    name
                    title
                    useEditableArea
                    transparentBackground
                    previewRegion {
                        ...RegionFields
                    }
                }
                previewAsset {
                    ...AssetFields
                }
                showModelOnFinishStep
                showPlusInVariantPrices
                showPricing
                skuCode
                stepGroups {
                    name
                    stepNames
                }
                steps {
                    stepName
                    type
                    tags
                    globalPropertyAspectConfigurations {
                        aspectName
                        globalPropertyConfigurationId
                    }
                    conversionConfigurationFieldLinks {
                        identifier
                        conversionConfigurationId
                    }
                    option {
                        ...OptionFields
                    }
                    stepTitle
                    helpText
                    mandatory
                    relevantPanelName
                    silent
                    conditions {
                        requiredVariantSelections
                        targetStepName
                        action
                    }
                    data {
                        __typename
                        ... on InformationStepData {
                            content
                            lookAtAnimation {
                                ...LookAtAnimationFields
                            }
                            modelAnimation {
                                ...ModelAnimationFields
                            }
                        }
                        ... on DigitalContentStepData {
                            advancedEditorAnimation {
                                x
                                y
                                zoom
                                layoutName
                            }
                            baseUrl
                            lookAtAnimation {
                                ...LookAtAnimationFields
                            }
                            modelAnimation {
                                ...ModelAnimationFields
                            }
                            regions {
                                ...RegionFields
                            }
                        }
                        ... on FrameStepData {
                            advancedEditorAnimation {
                                x
                                y
                                zoom
                                layoutName
                            }
                            colorOption {
                                ...OptionFields
                            }
                            colorPickerEnabled
                            initialZoomLevel
                            disablePlaceholder
                            displayImageOnFinishStep
                            displaySelectionOnFinishStep
                            forceImageCover
                            hideImageInCart
                            hideSelectionInCart
                            lookAtAnimation {
                                ...LookAtAnimationFields
                            }
                            maxColors
                            modelAnimation {
                                ...ModelAnimationFields
                            }
                            overlayImageKey
                            overlayImageUrl
                            placeholderImageKey
                            placeholderImageUrl
                            regions {
                                ...RegionFields
                            }
                            whitelistedExtensions
                        }
                        ... on IllustrationStepData {
                            advancedEditorAnimation {
                                x
                                y
                                zoom
                                layoutName
                            }
                            colorOption {
                                ...OptionFields
                            }
                            colorPickerEnabled
                            pmsPickerEnabled
                            displayColorsOnFinishStep
                            displaySelectionOnFinishStep
                            hideColorsInCart
                            hideSelectionInCart
                            lookAtAnimation {
                                ...LookAtAnimationFields
                            }
                            modelAnimation {
                                ...ModelAnimationFields
                            }
                            regions {
                                ...RegionFields
                            }
                        }
                        ... on MaterialStepData {
                            advancedEditorAnimation {
                                x
                                y
                                zoom
                                layoutName
                            }
                            displaySelectionOnFinishStep
                            hideSelectionInCart
                            lookAtAnimation {
                                ...LookAtAnimationFields
                            }
                            modelAnimation {
                                ...ModelAnimationFields
                            }
                            targetMaterials
                        }
                        ... on ModelStepData {
                            advancedEditorAnimation {
                                x
                                y
                                zoom
                                layoutName
                            }
                            displaySelectionOnFinishStep
                            hideSelectionInCart
                            lookAtAnimation {
                                ...LookAtAnimationFields
                            }
                            modelAnimation {
                                ...ModelAnimationFields
                            }
                            replaceProductModel
                        }
                        ... on ModuleStepData {
                            advancedEditorAnimation {
                                x
                                y
                                zoom
                                layoutName
                            }
                            displayTextOnFinishStep
                            hideTextInCart
                            lookAtAnimation {
                                ...LookAtAnimationFields
                            }
                            maxLength
                            modelAnimation {
                                ...ModelAnimationFields
                            }
                            module
                            regions {
                                ...RegionFields
                            }
                        }
                        ... on PictureStepData {
                            advancedEditorAnimation {
                                x
                                y
                                zoom
                                layoutName
                            }
                            displaySelectionOnFinishStep
                            hideSelectionInCart
                            lookAtAnimation {
                                ...LookAtAnimationFields
                            }
                            modelAnimation {
                                ...ModelAnimationFields
                            }
                            regions {
                                ...RegionFields
                            }
                        }
                        ... on ProductOverlayStepData {
                            regions {
                                ...RegionFields
                            }
                            overlayType
                        }
                        ... on QuestionStepData {
                            advancedEditorAnimation {
                                x
                                y
                                zoom
                                layoutName
                            }
                            allowMultipleSelections
                            displaySelectionsOnFinishStep
                            displayType
                            hideSelectionsInCart
                            lookAtAnimation {
                                ...LookAtAnimationFields
                            }
                            modelAnimation {
                                ...ModelAnimationFields
                            }
                        }
                        ... on ShapeStepData {
                            advancedEditorAnimation {
                                x
                                y
                                zoom
                                layoutName
                            }
                            displaySelectionOnFinishStep
                            excludeFromPrint
                            hideSelectionInCart
                            lookAtAnimation {
                                ...LookAtAnimationFields
                            }
                            modelAnimation {
                                ...ModelAnimationFields
                            }
                            regions {
                                ...RegionFields
                            }
                        }
                        ... on SilentIllustrationStepData {
                            advancedEditorAnimation {
                                x
                                y
                                zoom
                                layoutName
                            }
                            asset {
                                ...AssetFields
                            }
                            excludeFromPrint
                            lookAtAnimation {
                                ...LookAtAnimationFields
                            }
                            modelAnimation {
                                ...ModelAnimationFields
                            }
                            regions {
                                ...RegionFields
                            }
                        }
                        ... on TextStepData {
                            advancedEditorAnimation {
                                x
                                y
                                zoom
                                layoutName
                            }
                            allowNewlines
                            colorOption {
                                ...OptionFields
                            }
                            colorPickerEnabled
                            curved
                            defaultText
                            deleteDefaultOnFocus
                            displayColorOnFinishStep
                            displayImageFillOnFinishStep
                            displayStrokeOnFinishStep
                            displayTextOnFinishStep
                            hideColorInCart
                            hideImageFillInCart
                            hideSelectionInCart
                            hideStrokeInCart
                            hideTextInCart
                            imageFillOption {
                                ...OptionFields
                            }
                            imageFillScale
                            lookAtAnimation {
                                ...LookAtAnimationFields
                            }
                            maxLength
                            maxSize
                            minSize
                            modelAnimation {
                                ...ModelAnimationFields
                            }
                            paths
                            regions {
                                ...RegionFields
                            }
                            replaceableText
                            size
                            strokeEnabled
                            strokeOption {
                                ...OptionFields
                            }
                            strokeThickness
                            textAlign
                            uppercase
                            userCanReplaceText
                            vertical
                            verticalAlign
                        }
                    }
                }
            }
        }
    `;
};

export const getTransactionWithPartnerQuery = gql`
    query GetTransactionForPartner($id: String!) {
        transactions(ids: [$id]) {
            id
            completed
            bundle {
                id
            }
            customLogoLink
            isOrdered
            product {
                id
                partner {
                    id
                }
            }
        }
    }
`;

export const getWorkflowWithThemeQuery = gql`
    ${assetFragment(false)}
    query GetWorkflowForTheme($id: String!) {
        workflow(id: $id) {
            id
            overrideTheme {
                id
                configuration
                name
                primaryColor
                secondaryColor
                textColor
                themeLayout {
                    id
                    name
                }
                fontAsset {
                    ...AssetFields
                }
            }
        }
    }
`;

const marketplaceThemeInstallConfigurationFragment = gql`
    ${conversionConfigurationFragment}
    fragment MarketplaceThemeInstallConfigurationFields on MarketplaceThemeInstallConfiguration {
        id
        themeInstallId
        fields {
            type
            key
            value
            asset {
                key
                name
                fileLink
                type
            }
            conversionConfiguration {
                ...ConversionConfigurationFields
            }
            listValues
            objectValues
            schema
            root
        }
        themeVersion {
            id
            version
            status
            createdAt
            updatedAt
            launchData {
                targetFiles {
                    filename
                    type
                    mimeType
                    fileLink
                    preload
                }
            }
            configurationSchema {
                customSchemas {
                    name
                    type
                    title
                    fields
                    extends
                    schemas
                }
                fields {
                    key
                    type
                    title
                    required
                    defaultValue
                    options {
                        key
                        value
                    }
                    assetType
                    multiLine
                    limit
                    items
                    fields
                    schema
                    root
                }
            }
        }
    }
`;

export const getMarketplaceThemeInstallConfigurationQuery = gql`
    ${marketplaceThemeInstallConfigurationFragment}
    query GetMarketplaceThemeInstallConfiguration($themeConfigurationId: String!, $themeInstallId: String!) {
        marketplaceThemeInstallConfiguration(id: $themeConfigurationId, themeInstallId: $themeInstallId, raw: false) {
            ...MarketplaceThemeInstallConfigurationFields
        }
    }
`;

export const currentIntegrationQuery = gql`
    ${activeIntegrationFragment}
    ${marketplaceThemeInstallConfigurationFragment}
    query GetCurrentIntegration($themeConfigurationId: String) {
        currentIntegration {
            id
            type
            marketplaceThemeInstallConfiguration(id: $themeConfigurationId, raw: false) {
                ...MarketplaceThemeInstallConfigurationFields
            }
            ...ActiveIntegrationFields
        }
    }
`;

export const getTransactionForMarketplaceThemeQuery = gql`
    ${marketplaceThemeInstallConfigurationFragment}
    query ReadTransactionForMarketplaceTheme($id: String!) {
        transactions(ids: [$id]) {
            marketplaceThemeInstallConfiguration(raw: false) {
                ...MarketplaceThemeInstallConfigurationFields
            }
        }
    }
`;

export const getBundleForMarketplaceThemeQuery = gql`
    ${marketplaceThemeInstallConfigurationFragment}
    query ReadBundleForMarketplaceTheme($id: String!) {
        bundles(ids: [$id]) {
            marketplaceThemeInstallConfiguration(raw: false) {
                ...MarketplaceThemeInstallConfigurationFields
            }
        }
    }
`;

export const createOrderMutation = gql`
    mutation CreateOrder($orderItems: [OrderItemInput]!) {
        orderCreate(orderItems: $orderItems, paid: false, generateInternalId: true) {
            id
            internalId
        }
    }
`;

export const currencyConversionQuery = gql`
    query CurrencyConversion($from: String!, $to: String!) {
        currencyConversion(from: $from, to: $to) {
            rateFrom
            rateTo
            timestamp
        }
    }
`;
